import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Select, Button } from "@/components/kit";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import PetalCelebration from "@/components/PetalCelebration";

function calcAge(dob) {
  if (!dob) return null;
  const [y, m, d] = dob.split("-").map(Number);
  const t = new Date();
  let age = t.getFullYear() - y;
  if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) age--;
  return age;
}

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function GuardianRow({ g, idx, onChange, onRemove }) {
  const [matches, setMatches] = useState([]);
  const search = async () => {
    try {
      const r = await api.post("/membership/guardian-search", { name: g.name, email: g.email, phone: g.phone });
      setMatches(r.data.matches);
      if (r.data.matches.length === 0) toast.info("No matching member found — saved as typed-in guardian info.");
    } catch { toast.error("Search failed"); }
  };
  const pick = (m) => { onChange(idx, { ...g, name: m.name, email: m.email, member_id: m.member_id, member_number: m.member_number }); setMatches([]); };
  return (
    <div className="border border-ink/15 rounded-lg p-3 bg-cream space-y-2" data-testid={`guardian-row-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-bold uppercase tracking-wide text-ink/55">Guardian {idx + 1}{g.member_number ? ` · linked ${g.member_number}` : ""}</div>
        {idx > 0 && <button onClick={() => onRemove(idx)} className="text-maroon text-[13px]" data-testid={`remove-guardian-${idx}`}>Remove</button>}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid={`guardian-name-${idx}`} placeholder="Full name *" value={g.name} onChange={(e) => onChange(idx, { ...g, name: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
        <input data-testid={`guardian-phone-${idx}`} placeholder="Phone *" value={g.phone} onChange={(e) => onChange(idx, { ...g, phone: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
        <input data-testid={`guardian-email-${idx}`} placeholder="Email *" value={g.email} onChange={(e) => onChange(idx, { ...g, email: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
      </div>
      <div className="relative">
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid={`guardian-number-${idx}`} placeholder="RCA member # (optional)" value={g.member_number || ""} onChange={(e) => onChange(idx, { ...g, member_number: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]" />
          <button onClick={search} data-testid={`guardian-search-${idx}`} className="text-[13px] font-semibold border border-ink/20 rounded-lg px-3 py-2 min-h-[40px]">Find existing member</button>
          {g.member_id && <span className="text-[12px] text-dark-olive font-semibold flex items-center gap-1"><Icon name="Link2" className="w-3.5 h-3.5" /> Linked</span>}
        </div>
        {matches.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 space-y-1 bg-cream border border-ink/15 rounded-lg p-1 shadow-lg max-h-52 overflow-y-auto" data-testid={`guardian-matches-${idx}`}>
            {matches.map((m) => (
              <button key={m.member_id} onClick={() => pick(m)} data-testid={`guardian-match-${m.member_number}`} className="w-full text-left text-[14px] px-3 py-2 rounded bg-olive/15 border border-olive/40 hover:bg-olive/25">
                {m.name} · {m.member_number} · {m.email}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function JoinWizard({ adminMode = false, onDone, onClose, prefill }) {
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState("dob");
  const [celebrate, setCelebrate] = useState(null);
  const [dob, setDob] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", pin: "", account_type: "", billing_cadence: "", code: "", nextgen_optin: false, tags: [],
    street: "", city: "", state: "", zip: "",
    org_name: "", org_street: "", org_city: "", org_state: "", org_zip: "", pastor_name: "", org_type: "", org_description: "", org_classification: "" });
  const [guardians, setGuardians] = useState([{ name: "", phone: "", email: "", member_number: "" }]);
  const [family, setFamily] = useState([]);
  const [codeState, setCodeState] = useState(null);
  const [saving, setSaving] = useState(false);
  const age = calcAge(dob);

  useEffect(() => { api.get("/membership/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (prefill?.name) setForm((f) => ({ ...f, name: prefill.name })); }, [prefill]);

  const isYouth = age !== null && age <= 17;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const proceedFromDob = () => {
    if (!dob) return toast.error("Enter a date of birth.");
    if (age === null || age < 0 || age > 120) return toast.error("Enter a valid date of birth.");
    if (age <= 17) { set("account_type", "nextgen_youth"); }
    setStep("details");
  };

  const checkCode = async () => {
    if (!form.code.trim()) return;
    try {
      const r = await api.post("/membership-codes/validate", { code: form.code });
      if (r.data.valid) { setCodeState(r.data); toast.success(r.data.type === "full_waiver" ? `Full waiver (${r.data.reason})` : `${r.data.percent}% discount`); }
      else { setCodeState(null); toast.error("Code invalid or already used."); }
    } catch { toast.error("Could not check code"); }
  };

  const dueAmount = () => {
    if (!cfg || !form.account_type || !form.billing_cadence) return 0;
    const t = cfg.dues.types?.[form.account_type] || {};
    let amt = form.billing_cadence === "monthly" ? t.monthly_cents : t.yearly_cents;
    if (codeState?.type === "percent_discount") amt = Math.round(amt * (100 - codeState.percent) / 100);
    if (codeState?.type === "full_waiver") amt = 0;
    return amt || 0;
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required.");
    if (!/^\d{4}$/.test(form.pin)) return toast.error("PIN must be 4 digits.");
    if (!form.phone.trim()) return toast.error("A phone number is required.");
    if (!form.street.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim()) return toast.error("A full home address is required.");
    if (!isYouth && !form.account_type) return toast.error("Choose an account type.");
    const isOrg = ["business", "organization", "church"].includes(form.account_type);
    if (isOrg) {
      if (!form.org_name.trim()) return toast.error(`The ${form.account_type}'s own name is required.`);
      if (!form.org_street.trim() || !form.org_city.trim() || !form.org_state.trim() || !form.org_zip.trim()) return toast.error("The organization's full address is required.");
      if (form.account_type === "church" && !form.pastor_name.trim()) return toast.error("The pastor's name is required.");
      if (["business", "organization"].includes(form.account_type)) {
        if (!form.org_type.trim() || !form.org_description.trim()) return toast.error("A type and short description are required.");
        if (!form.org_classification) return toast.error("Choose a classification.");
      }
    }
    if (isYouth) {
      const ok = guardians.every((g) => g.name.trim() && g.phone.trim() && g.email.trim());
      if (!ok) return toast.error("Every guardian needs a name, phone, and email.");
    }
    const payload = { dob, account_type: form.account_type, name: form.name, email: form.email, phone: form.phone, pin: form.pin, tags: form.tags,
      street: form.street, city: form.city, state: form.state, zip: form.zip,
      org_name: form.org_name, org_street: form.org_street, org_city: form.org_city, org_state: form.org_state, org_zip: form.org_zip,
      pastor_name: form.pastor_name, org_type: form.org_type, org_description: form.org_description, org_classification: form.org_classification,
      billing_cadence: form.billing_cadence || "none", code: form.code || null, nextgen_optin: form.nextgen_optin, guardians: isYouth ? guardians : [],
      family: isOrg ? [] : family.filter((f) => (f.name || "").trim()) };
    setSaving(true);
    try {
      await api.post(adminMode ? "/membership/members" : "/membership/join", payload);
      if (adminMode) { toast.success("Member added"); onDone && onDone(); }
      else { setCelebrate(cfg.celebration_message || "Welcome! You're a member!"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not complete signup"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-6 text-ink/60">Loading…</div>;
  const monthlyOn = cfg.dues.monthly_enabled, yearlyOn = cfg.dues.yearly_enabled;
  const promo = cfg.promo_free;

  return (
    <div className="max-w-2xl" data-testid="join-wizard">
      {celebrate && <PetalCelebration message={celebrate} onContinue={() => { setCelebrate(null); onDone && onDone(); }} />}
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-zilla text-3xl font-semibold">{adminMode ? "Add a member" : "Join Rosemoor"}</h1>
        {onClose && <button onClick={onClose} data-testid="join-close" className="text-ink/60"><Icon name="X" className="w-6 h-6" /></button>}
      </div>

      {step === "dob" && (
        <div className="bg-cream border border-ink/15 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2"><Icon name="Cake" className="w-5 h-5 text-maroon" /><h2 className="font-zilla text-xl font-semibold">First, your date of birth</h2></div>
          <p className="text-[15px] text-ink/70">We use this to set up the right kind of account.</p>
          <input type="date" data-testid="dob-input" value={dob} onChange={(e) => setDob(e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
          {age !== null && age >= 0 && <div className="text-[14px] text-ink/60" data-testid="computed-age">Age: {age}{age <= 17 ? " · Next Gen Youth account" : ""}</div>}
          <Button onClick={proceedFromDob} data-testid="dob-continue" className="px-6 py-3 min-h-[48px]">Continue</Button>
        </div>
      )}

      {step === "details" && (
        <div className="space-y-4">
          {promo && (
            <div className="bg-olive/15 border border-olive/40 rounded-lg p-4" data-testid="promo-free-banner">
              <div className="font-zilla text-lg font-semibold text-dark-olive">Free membership right now</div>
              <p className="text-[15px] text-ink/75 mt-0.5">{promo.note || "Your dues are waived — welcome to Rosemoor!"}</p>
            </div>
          )}
          {isYouth && (
            <div className="bg-gold/15 border border-gold/40 rounded-lg p-4 text-[15px]" data-testid="youth-banner">
              This is a <strong>Next Gen Youth</strong> account (age {age}). A guardian is required.
            </div>
          )}

          {!isYouth && (
            <div className="bg-cream border border-ink/15 rounded-xl p-5">
              <h2 className="font-zilla text-lg font-semibold mb-2">Account type</h2>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="type-select">
                {cfg.account_types.filter((t) => t.adult_selectable).map((t) => (
                  <button key={t.id} data-testid={`type-${t.id}`} onClick={() => set("account_type", t.id)}
                    className={`text-left px-4 py-3 rounded-lg border min-h-[52px] ${form.account_type === t.id ? "bg-olive/20 border-olive/50" : "border-ink/15 hover:bg-warm-cream"}`}>
                    <div className="font-semibold text-[15px]">{t.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3">
            <h2 className="font-zilla text-lg font-semibold">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              <input data-testid="member-name" placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <input data-testid="member-email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <input data-testid="member-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <input data-testid="member-pin" type="password" placeholder="Create a 4-digit PIN *" inputMode="numeric" maxLength={4} value={form.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
            </div>
            <div className="text-[13px] font-semibold text-ink/55 pt-1">Home address</div>
            <input data-testid="addr-street" placeholder="Street address *" value={form.street} onChange={(e) => set("street", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <input data-testid="addr-city" placeholder="City *" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <input data-testid="addr-state" placeholder="State *" value={form.state} onChange={(e) => set("state", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <input data-testid="addr-zip" placeholder="ZIP *" value={form.zip} onChange={(e) => set("zip", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
            </div>
          </div>

          {!["business", "organization", "church"].includes(form.account_type) && (
            <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3" data-testid="family-section">
              <h2 className="font-zilla text-lg font-semibold">Family / household members</h2>
              <p className="text-[13px] text-ink/60">Optional — add family members who live with you (name, relation, and year of birth).</p>
              {family.map((fm, i) => (
                <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-center" data-testid={`family-input-row-${i}`}>
                  <input data-testid={`family-name-${i}`} value={fm.name} onChange={(e) => setFamily((fs) => fs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Full name" className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
                  <Select data-testid={`family-relation-${i}`} value={fm.relation} onChange={(e) => setFamily((fs) => fs.map((x, j) => (j === i ? { ...x, relation: e.target.value } : x)))} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
                    <option value="">Relation…</option>
                    {["Spouse / Partner", "Child", "Parent", "Sibling", "Grandparent", "Grandchild", "Other relative", "Roommate", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                  <input data-testid={`family-yob-${i}`} value={fm.year_of_birth} onChange={(e) => setFamily((fs) => fs.map((x, j) => (j === i ? { ...x, year_of_birth: e.target.value.replace(/\D/g, "").slice(0, 4) } : x)))} placeholder="Year of birth" inputMode="numeric" maxLength={4} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] sm:w-32" />
                  <button data-testid={`family-remove-${i}`} onClick={() => setFamily((fs) => fs.filter((_, j) => j !== i))} className="text-maroon text-[13px] font-semibold px-2">Remove</button>
                </div>
              ))}
              <button data-testid="family-add-row" onClick={() => setFamily((fs) => [...fs, { name: "", relation: "", year_of_birth: "" }])} className="text-[14px] font-semibold border border-ink/20 rounded-lg px-3 py-2 min-h-[40px]">+ Add family member</button>
            </div>
          )}

          {["business", "organization", "church"].includes(form.account_type) && (
            <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3" data-testid="org-fields">
              <h2 className="font-zilla text-lg font-semibold capitalize">{form.account_type} details</h2>
              <input data-testid="org-name" placeholder={`${form.account_type === "church" ? "Church" : form.account_type === "business" ? "Business" : "Organization"} name *`} value={form.org_name} onChange={(e) => set("org_name", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <input data-testid="org-street" placeholder="Street address *" value={form.org_street} onChange={(e) => set("org_street", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <input data-testid="org-city" placeholder="City *" value={form.org_city} onChange={(e) => set("org_city", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
                <input data-testid="org-state" placeholder="State *" value={form.org_state} onChange={(e) => set("org_state", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
                <input data-testid="org-zip" placeholder="ZIP *" value={form.org_zip} onChange={(e) => set("org_zip", e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              </div>
              {form.account_type === "church" && (
                <input data-testid="org-pastor" placeholder="Pastor's name *" value={form.pastor_name} onChange={(e) => set("pastor_name", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
              )}
              {["business", "organization"].includes(form.account_type) && (
                <>
                  <input data-testid="org-type" placeholder="Type of business/organization *" value={form.org_type} onChange={(e) => set("org_type", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
                  <textarea data-testid="org-description" rows={2} placeholder="Short description of what you do *" value={form.org_description} onChange={(e) => set("org_description", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 text-[15px]" />
                  <Select data-testid="org-classification" value={form.org_classification} onChange={(e) => set("org_classification", e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
                    <option value="">Classification *</option>
                    <option value="non-profit">Non-profit</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation</option>
                    <option value="school">School</option>
                  </Select>
                </>
              )}
            </div>
          )}

          {isYouth && (
            <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3">
              <h2 className="font-zilla text-lg font-semibold">Guardian information</h2>
              {guardians.map((g, i) => (
                <GuardianRow key={i} g={g} idx={i} onChange={(idx, ng) => setGuardians((gs) => gs.map((x, j) => (j === idx ? ng : x)))} onRemove={(idx) => setGuardians((gs) => gs.filter((_, j) => j !== idx))} />
              ))}
              <button data-testid="add-guardian" onClick={() => setGuardians((gs) => [...gs, { name: "", phone: "", email: "", member_number: "" }])} className="text-[14px] font-semibold border border-ink/20 rounded-lg px-3 py-2 min-h-[40px]">+ Add another guardian</button>
            </div>
          )}

          {!isYouth && promo && (
            <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3" data-testid="promo-dues-waived">
              <h2 className="font-zilla text-lg font-semibold">Dues</h2>
              <div className="text-[15px]" data-testid="due-amount">Amount due: <strong>Waived</strong> — {promo.note || "Promotional free membership"}</div>
              {age >= 18 && age <= 24 && (
                <label className="flex items-center gap-2 text-[15px]" data-testid="nextgen-optin-label">
                  <input type="checkbox" data-testid="nextgen-optin" checked={form.nextgen_optin} onChange={(e) => set("nextgen_optin", e.target.checked)} className="w-5 h-5 accent-maroon" />
                  {"I'd also like to join Next Gen programming for youth-oriented activities"}
                </label>
              )}
            </div>
          )}

          {!isYouth && !promo && (monthlyOn || yearlyOn) && (
            <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3">
              <h2 className="font-zilla text-lg font-semibold">Dues</h2>
              <div className="flex gap-2" data-testid="cadence-select">
                {monthlyOn && <button data-testid="cadence-monthly" onClick={() => set("billing_cadence", "monthly")} className={`px-4 py-2.5 rounded-lg border min-h-[44px] text-[15px] font-semibold ${form.billing_cadence === "monthly" ? "bg-ink text-cream" : "border-ink/20"}`}>Monthly</button>}
                {yearlyOn && <button data-testid="cadence-yearly" onClick={() => set("billing_cadence", "yearly")} className={`px-4 py-2.5 rounded-lg border min-h-[44px] text-[15px] font-semibold ${form.billing_cadence === "yearly" ? "bg-ink text-cream" : "border-ink/20"}`}>Yearly</button>}
              </div>
              <div>
                <label className="text-[14px] font-semibold">Have a code? Enter it before paying</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="code-input" placeholder="RCA-XXXXXX" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
                  <button data-testid="apply-code" onClick={checkCode} className="border border-ink/20 rounded-lg px-4 min-h-[44px] text-[15px] font-semibold">Apply</button>
                </div>
                {codeState && <div className="text-[13px] text-dark-olive font-semibold mt-1" data-testid="code-applied">{codeState.type === "full_waiver" ? `Full waiver — ${codeState.reason}` : `${codeState.percent}% off`}</div>}
              </div>
              {form.billing_cadence && (
                <div className="text-[15px]" data-testid="due-amount">Amount due: <strong>{dueAmount() === 0 ? (codeState?.type === "full_waiver" ? "Waived" : "$0.00") : dollars(dueAmount())}</strong> / {form.billing_cadence}</div>
              )}
              {age >= 18 && age <= 24 && (
                <label className="flex items-center gap-2 text-[15px] mt-2" data-testid="nextgen-optin-label">
                  <input type="checkbox" data-testid="nextgen-optin" checked={form.nextgen_optin} onChange={(e) => set("nextgen_optin", e.target.checked)} className="w-5 h-5 accent-maroon" />
                  {"I'd also like to join Next Gen programming for youth-oriented activities"}
                </label>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep("dob")} className="border border-ink/20 rounded-lg px-5 py-3 min-h-[48px] text-[15px] font-semibold">Back</button>
            <button onClick={submit} disabled={saving} data-testid="join-submit" className="bg-maroon text-cream font-semibold rounded-lg px-6 py-3 min-h-[48px] text-[15px] disabled:opacity-40">
              {saving ? "Saving…" : adminMode ? "Add member" : "Complete signup"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
